import { isEqual, omit, pick } from 'lodash-es';
import { createJSONStorage } from 'zustand/middleware';
import { CallIntelTypes } from '@frontend/api-call-intel';
import { createShallowStore, createStoreWithPersistAndSubscribe } from '@frontend/store';
import {
  BASE_FILTER_KEYS,
  BIDIRECTIONAL_FILTER_KEYS,
  DEFAULT_CALL_INTEL_TIME_PERIOD,
  LOCATION_DEPENDENT_FILTER_KEYS,
} from '../constants';
import type { FilterValues } from '../filters/types';

export type CallIntelSubViewType = 'appointment-type' | 'category' | 'follow-up' | 'sentiment' | 'service-quality';

export type CallIntelSubViewId =
  | string
  | CallIntelTypes.AppointmentTypeEnum
  | CallIntelTypes.CategoryEnum
  | CallIntelTypes.SentimentEnum
  | CallIntelTypes.ServiceQualityFlag;

export type CallIntelSubView = {
  id?: CallIntelSubViewId;
  type?: CallIntelSubViewType;
};

export type MultiLocationsView = 'summary' | 'betweenLocations';

interface CallIntelStore {
  activeMultiView?: MultiLocationsView;
  activeTab: CallIntelTypes.CallIntelTab;
  autoResetFilters: Partial<Record<keyof FilterValues, boolean>>;
  filterHintText: string;
  filters: FilterValues;
  locationIds: string[];
  subView: CallIntelSubView;
  subViewFilters: FilterValues;
  urlOverrideFilters: FilterValues;
  isLoadingAggregatedData: boolean;

  setActiveMultiView: (activeMultiView: MultiLocationsView) => void;
  setActiveTab: (activeTab: CallIntelTypes.CallIntelTab) => void;
  setFilterHintText: (filterHintText: string) => void;
  setFilters: (filters: Partial<FilterValues>) => void;
  updateFilters: (updatedFilters: FilterValues) => void;
  setLocationIds: (locationId: string[]) => void;
  setSubView: (subView: CallIntelSubView) => void;
  setSubViewFilters: (subViewFilters: FilterValues) => void;
  updateSubViewFilters: (updatedFilters: FilterValues) => void;
  setIsLoadingAggregatedData: (isLoading: boolean) => void;
  setAutoResetFilters: (autoResetFilters: Partial<Record<keyof FilterValues, boolean>>) => void;
  setUrlOverrideFilters: (urlOverrideFilters: FilterValues) => void;
  clearUrlOverrideFilters: () => void;
  applyUrlOverrideFilters: (updatedValues: FilterValues) => void;
  resetFilters: () => void;
  resetSubViewFilters: () => void;
  resetStore: () => void;
}

export const useCallIntelStore = createStoreWithPersistAndSubscribe<CallIntelStore>(
  (set, get) => ({
    activeTab: 'overview',
    autoResetFilters: {},
    filterHintText: '',
    filters: {},
    isLoadingAggregatedData: false,
    locationIds: [],
    selectedTimePeriodKey: DEFAULT_CALL_INTEL_TIME_PERIOD,
    subView: {},
    subViewFilters: {},
    urlOverrideFilters: {},
    trayFilters: {},
    setActiveTab: (activeTab) => {
      set({ activeTab });
    },
    setActiveMultiView: (activeMultiView) => {
      set({ activeMultiView });
    },

    setFilterHintText: (filterHintText) => {
      set({ filterHintText });
    },
    setFilters: (filters) => {
      set({
        filters,
      });
    },
    setLocationIds: (locationIds) => {
      set({ locationIds });
    },
    setSubView: (subView) => {
      if (subView.id) {
        set({
          subView,
          subViewFilters: pick(get().filters, BASE_FILTER_KEYS), // Initialize with base filters only
        });
      } else {
        set({
          subView,
          subViewFilters: {}, // Clear subViewFilters when subView is cleared
        });
      }
    },

    setSubViewFilters: (subViewFilters) => {
      set({ subViewFilters });
    },
    setIsLoadingAggregatedData: (isLoading) => {
      set({ isLoadingAggregatedData: isLoading });
    },
    setAutoResetFilters: (autoResetFilters) => {
      set({ autoResetFilters });
    },
    resetFilters: () => {
      set({
        filters: {},
      });
    },
    resetSubViewFilters() {
      set({
        subViewFilters: {},
      });
    },
    updateFilters: (updatedFilters) => {
      const currentFilters = get().filters;
      const locationsChanged = updatedFilters.locations && !isEqual(updatedFilters.locations, currentFilters.locations);

      set({
        filters: {
          ...currentFilters,
          // When locations change, clear filters whose values are scoped to specific locations
          ...(locationsChanged && Object.fromEntries(LOCATION_DEPENDENT_FILTER_KEYS.map((key) => [key, undefined]))),
          ...updatedFilters,
        },
      });
    },
    updateSubViewFilters: (updatedFilters) => {
      const currentSubViewFilters = get().subViewFilters;
      const currentFilters = get().filters;

      // Sync bidirectional filter keys back to global filters
      const bidirectionalUpdates = pick(updatedFilters, BIDIRECTIONAL_FILTER_KEYS);
      const hasBidirectionalChanges = Object.keys(bidirectionalUpdates).length > 0;

      set({
        subViewFilters: {
          ...currentSubViewFilters,
          ...updatedFilters,
        },
        ...(hasBidirectionalChanges && {
          filters: {
            ...currentFilters,
            ...bidirectionalUpdates,
          },
        }),
      });
    },
    setUrlOverrideFilters: (urlOverrideFilters) => {
      set({ urlOverrideFilters });
    },
    clearUrlOverrideFilters: () => {
      set({ urlOverrideFilters: {} });
    },
    applyUrlOverrideFilters: (updatedValues) => {
      const { urlOverrideFilters, subView } = get();
      const merged = { ...urlOverrideFilters, ...updatedValues };
      if (subView.id) {
        set({ subViewFilters: merged, urlOverrideFilters: {} });
      } else {
        set({ filters: merged, urlOverrideFilters: {} });
      }
    },
    resetStore: () => {
      set({
        activeMultiView: 'summary',
        autoResetFilters: {},
        filterHintText: '',
        filters: {},
        urlOverrideFilters: {},
        subView: {},
      });
    },
    // This method is used to reset the store from resetAllStores helper, generally triggered on sign-out
    reset: () => {
      get().resetStore();
    },
  }),
  {
    name: 'call-intel-store',
    partialize: (state) =>
      ({
        filters: omit(state.filters, ['locations']),
      } as CallIntelStore),
    storage: createJSONStorage(() => localStorage),
  },
  {
    name: 'CallIntelStore',
    trace: true,
  }
);

export const useCallIntelShallowStore = createShallowStore<CallIntelStore>(useCallIntelStore);
